import { useEffect, useRef, useState } from "react";
import { unitById } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { useBalances } from "../state/balances.js";
import { Button } from "../ui/Button.js";
import { portraitUrl } from "../assets/manifest.js";
import { workshopApi } from "./api.js";
import { RevealScene } from "./RevealScene.js";
import { useUnits } from "./state.js";
import { Stars, TierBadge, tierColor, unitName } from "./shared.js";
import type { BannerView, PullView } from "./types.js";
import { audio } from "../audio/engine.js";
import { WorkshopOffers } from "./WorkshopOffers.js";
import { RevealAutoEquip } from "./RevealAutoEquip.js";
import { useSession } from "../state/session.js";
import { onWs } from "../api/ws.js";
import { useServerTick } from "../ui/Timer.js";
import { serverNow } from "../state/serverTime.js";

/** Illustrated offers with authoritative prices, guarantees and rotation (R27). */
export function Workshop() {
  const t = useT();
  const banners = useUnits((s) => s.banners);
  const refreshWorkshop = useUnits((s) => s.refreshWorkshop);
  const run = useUnits((s) => s.run);
  const setReveal = useUnits((s) => s.setReveal);
  const balances = useBalances((s) => s.balances);
  const now = useServerTick(1);
  const vipLevel = useSession(s => s.vip?.level ?? 0);
  const pending = useRef(false), [busy, setBusy] = useState(false);
  const nextRotation = Math.min(...(banners ?? []).map((banner) => banner.rotationEndsAt ?? Infinity));
  useEffect(() => {
    // A push can invalidate a read even after the event burst has settled.
    // Keep a trailing refresh until every push during that read is covered.
    let queued: ReturnType<typeof setTimeout> | undefined;
    let reading = false, dirty = false, disposed = false;
    const refresh = () => {
      dirty = true;
      clearTimeout(queued);
      if (reading || disposed) return;
      queued = setTimeout(() => {
        reading = true; dirty = false;
        void refreshWorkshop().finally(() => {
          reading = false;
          if (dirty && !disposed) refresh();
        });
      }, 100);
    };
    const relevant = new Set(["event.start", "event.end", "event.extended", "day.new", "ready"]);
    const off = onWs("*", event => { if (relevant.has(event.type) || (reading && event.id > 0)) refresh(); });
    refresh();
    const poll = setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    return () => { disposed = true; off(); clearTimeout(queued); clearInterval(poll); window.removeEventListener("focus", refresh); };
  }, [refreshWorkshop]);
  useEffect(() => {
    if (!Number.isFinite(nextRotation)) return;
    const timeout = setTimeout(() => void refreshWorkshop(), Math.max(0, nextRotation - serverNow()) + 25);
    return () => clearTimeout(timeout);
  }, [nextRotation, refreshWorkshop]);
  if (!banners) return <p className="dim">{t("common.loading")}</p>;

  const pull = async (b: BannerView, count: 1 | 10, payWith: "token" | "credits" | "free") => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try {
      const res = await run(() => workshopApi.pull(b.id, count, payWith, b.rotationKey));
      await refreshWorkshop();
      if (res) setReveal({ pulls: res.pulls, banner: res.banner });
    } finally { pending.current = false; setBusy(false); }
  };
  return <WorkshopOffers banners={banners} now={now} credits={balances?.credits ?? 0} vipLevel={vipLevel} busy={busy} onPull={pull} />;
}

/** Full-screen reveal: one frame after another in the chamber; skip jumps to the summary grid (R27). */
export function RevealOverlay() {
  const t = useT();
  const reveal = useUnits((s) => s.reveal);
  const setReveal = useUnits((s) => s.setReveal);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const scene = useRef<RevealScene | null>(null);
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);
  const [standing, setStanding] = useState(false);
  const pulls = reveal?.pulls ?? [];
  const current: PullView | undefined = pulls[index];

  useEffect(() => {
    if (!reveal || !canvas.current) return;
    const s = new RevealScene(canvas.current);
    scene.current = s;
    const ro = new ResizeObserver(() => s.resize());
    ro.observe(canvas.current);
    setIndex(0);
    setDone(false);
    return () => {
      ro.disconnect();
      s.dispose();
      scene.current = null;
    };
  }, [reveal]);

  useEffect(() => {
    if (!reveal || !current || done) return;
    setStanding(false);
    scene.current?.reveal(current.unitId, tierColor(current.tier), current.tier === "S", () => setStanding(true));
  }, [reveal, index, done, current]);

  useEffect(() => {
    if (!done) return;
    scene.current?.silence();
    audio.play("reward");
  }, [done]);

  if (!reveal || !current) return null;
  const def = unitById(current.unitId);
  const next = () => {
    if (index + 1 < pulls.length) setIndex(index + 1);
    else setDone(true);
  };
  return (
    <div className="reveal" data-testid="reveal">
      <canvas ref={canvas} className="reveal-canvas" style={{ display: done ? "none" : "block" }} />
      {!done ? (
        <div className="reveal-ui">
          <div className="reveal-top row">
            <span className="badge">{index + 1} / {pulls.length}</span>
            <span className="grow" />
            <Button style={{ minHeight: 34 }} onClick={() => (pulls.length > 1 ? setDone(true) : next())} data-testid="reveal-skip">
              {t("workshop.skip")}
            </Button>
          </div>
          <div className={`reveal-card fade-in ${standing ? "show" : ""}`} style={{ borderColor: tierColor(current.tier) }} onClick={() => (standing ? next() : scene.current?.skip())} data-testid="reveal-card">
            <div className="row" style={{ gap: 8 }}>
              <TierBadge tier={current.tier} />
              <strong style={{ color: tierColor(current.tier), fontSize: 18 }}>{unitName(current.unitId)}</strong>
              {current.isNew ? <span className="badge ok" data-testid="reveal-new">NEW</span> : <span className="badge">+{current.fragments} FRG</span>}
              {current.flags.hardPity ? <span className="badge gold">PITY</span> : null}
              {current.flags.featuredWin ? <span className="badge gold">FEATURED</span> : null}
            </div>
            <div className="hint" style={{ marginTop: 4 }}>
              {t(`role.${def.role}`)} · {t(`chassis.${def.chassis}`)} · {def.mfr}
            </div>
            <p className="dim" style={{ fontSize: 13, margin: "6px 0 0" }}>{pick(def.bio)}</p>
            <div className="hint" style={{ marginTop: 6 }}>{standing ? t("workshop.tapNext") : t("workshop.tapSkip")}</div>
          </div>
        </div>
      ) : (
        <div className="reveal-summary glass fade-in" data-testid="reveal-summary">
          <h2 style={{ margin: "0 0 10px" }}>{t("workshop.result", { n: pulls.length })}</h2>
          <div className="reveal-grid">
            {pulls.map((p, i) => (
              <div key={i} className="reveal-tile" style={{ borderColor: tierColor(p.tier), background: `linear-gradient(180deg, ${tierColor(p.tier)}22, rgba(0,0,0,0.5))` }} data-testid={`reveal-tile-${p.tier}`}>
                <img src={portraitUrl(p.unitId)} alt={unitName(p.unitId)} draggable={false} />
                <TierBadge tier={p.tier} style={{ position: "absolute", top: 4, left: 4 }} />
                {p.isNew ? <span className="badge ok reveal-new">NEW</span> : <span className="badge reveal-new">+10 FRG</span>}
                <div className="ellipsis" style={{ fontSize: 11, color: tierColor(p.tier), fontWeight: 700 }}>
                  {unitName(p.unitId)}
                </div>
                <Stars stars={0} size={8} />
              </div>
            ))}
          </div>
          <RevealAutoEquip pulls={pulls} />
          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <span className="badge gold">{t("workshop.pity", { n: reveal.banner.pity.sinceS, max: reveal.banner.pity.hardPity })}</span>
            <span className="grow" />
            <Button variant="primary" onClick={() => setReveal(null)} data-testid="reveal-close">
              {t("common.close")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
